import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { type GeoLocation, type GeoLocationsList, GeoLocationTypes } from '@openx/types/geoLocation.ts';
import type { GeographicTargetingState } from '@openx/types/targeting/geographic.ts';
import isEmpty from 'lodash/isEmpty';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkAdd } from '../../shared/BulkAdd/useBulkAdd.ts';
import { convertGeoObjToArray } from '../utils.ts';
import { BulkAdd } from './BulkAdd.tsx';
import { GeographicAutoComplete } from './GeographicAutoComplete.tsx';
import { GeographicType } from './GeographicType.tsx';

const StyledIconButton = styled(IconButton)`
  margin-left: ${({ theme }) => theme.spacing(1)};
`;

type LocationProps = {
  targetingParams: GeographicTargetingState;
  children?: ReactNode;
  onBulkApply: (items: GeoLocationsList) => void;
  onSelect: (item: GeoLocation) => void;
};

export function Location({ targetingParams, children, onBulkApply, onSelect }: LocationProps) {
  const { t } = useTranslation();

  const [geographicType, setGeographicType] = useState<GeoLocationTypes>(GeoLocationTypes.CONTINENT);

  const { isBulkOpen, onBulkAdd, onBulkClose, onBulkOpen } = useBulkAdd<GeoLocationsList>({ onAdd: onBulkApply });
  const bulkDisabled =
    !isEmpty(targetingParams.excludes_subset) || !Object.values(targetingParams.excludes).every(isEmpty);
  const currentItems = !isEmpty(targetingParams.includes)
    ? convertGeoObjToArray(targetingParams.includes)
    : convertGeoObjToArray(targetingParams.excludes);

  return (
    <>
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: 'flex-end',
        }}
      >
        <Grid size={3}>{children}</Grid>
        <Grid size={9}>
          <Grid
            container
            spacing={1}
            sx={{
              alignItems: 'flex-end',
            }}
          >
            <Grid size={3}>
              <GeographicType value={geographicType} onChange={setGeographicType} />
            </Grid>
            <Grid size={9} data-test="include-location">
              <GeographicAutoComplete
                targetingParams={targetingParams}
                onSelect={onSelect}
                geographicType={geographicType}
                inputLabel={t('Location')}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        sx={{
          alignItems: 'center',
          marginTop: theme => theme.spacing(1),
        }}
      >
        <Button onClick={onBulkOpen} disabled={bulkDisabled}>
          {t('Bulk Add')}
        </Button>
        <Tooltip
          title={t('Postal code only. Bulk Add is only available when there are no exclusions.')}
          data-test="bulk-icon"
        >
          <StyledIconButton size="small">
            <InfoRoundedIcon fontSize="small" />
          </StyledIconButton>
        </Tooltip>
      </Grid>
      {isBulkOpen && <BulkAdd onClose={onBulkClose} onBulkAdd={onBulkAdd} currentItems={currentItems} />}
    </>
  );
}
